/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Card, Form, Spinner, Table,
} from 'react-bootstrap';
import Error from '../../Components/Error';
import AccountInputModal from './Components/AccountInputModal';
import AccountsService from '../../Services/AccountsService';
import { invalidateAccountsCache } from '../../Hooks/useAccountsCache';

// Kind → bootstrap badge variant. Cash gets a neutral colour because
// it's the default state of the world; bank gets blue because it's the
// most common admin-managed account; payment_gateway gets info because
// it's automated-rail money.
const KIND_VARIANTS = {
  cash: 'secondary',
  bank: 'primary',
  payment_gateway: 'info',
};

const KIND_LABELS = {
  cash: 'Cash',
  bank: 'Bank',
  payment_gateway: 'Payment gateway',
};

function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showInactive, setShowInactive] = useState(false);
  const [kindFilter, setKindFilter] = useState('');

  // Modal state: null when create-mode is closed, an Account object
  // when edit-mode is open, true when create-mode is open. We use a
  // dedicated state instead of useModalState because we need to
  // distinguish "no account → create" from "with account → edit".
  const [modalAccount, setModalAccount] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      // We fetch ALL accounts (including inactive) up-front so the
      // "Show inactive" toggle can switch client-side without an extra
      // round trip. There are not enough accounts to make this slow
      // (typically <20).
      const list = await AccountsService.list({});
      setAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const visibleAccounts = useMemo(() => accounts.filter((a) => {
    if (!showInactive && !a.is_active) return false;
    if (kindFilter && a.kind !== kindFilter) return false;
    return true;
  }), [accounts, showInactive, kindFilter]);

  const handleOpenCreate = () => {
    setModalAccount(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (account) => {
    setModalAccount(account);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setModalAccount(null);
  };

  const handleSaved = () => {
    // After a create/edit: invalidate the module-level cache so other
    // surfaces (Payments tables, picker dropdowns) see fresh data, and
    // refetch this page's local list.
    invalidateAccountsCache();
    fetchAccounts();
    handleClose();
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h1 className="page-title mb-1">Accounts</h1>
          <div className="text-muted">
            Where money moves through. Used to categorize Payments by source/destination.
          </div>
        </div>
        <Button variant="primary" onClick={handleOpenCreate}>
          <i className="fa fa-plus me-2" />
          Add Account
        </Button>
      </div>

      <Error error={error} setError={setError} />

      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <div className="row g-2 align-items-end">
            <Form.Group className="col-md-4 col-sm-6" controlId="acc_filter_kind">
              <Form.Label className="small text-muted mb-1">Kind</Form.Label>
              <Form.Select
                size="sm"
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
                <option value="payment_gateway">Payment gateway</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-4 col-sm-6" controlId="acc_show_inactive">
              <Form.Check
                type="checkbox"
                label="Show inactive"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
          <span className="ms-2 text-muted">Loading accounts…</span>
        </div>
      ) : (
        <Table responsive striped bordered hover className="align-middle responsive-card-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Provider</th>
              <th>Currency</th>
              <th>Identifier</th>
              <th>Active</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleAccounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  {accounts.length === 0 ? (
                    <>
                      <i className="fa fa-piggy-bank d-block mb-2" style={{ fontSize: '1.5rem' }} />
                      No accounts yet. Click &quot;Add Account&quot; to create your first one
                      {' '}
                      (e.g. Cash HUF, Stripe, K&amp;H Business).
                    </>
                  ) : (
                    'No accounts match these filters.'
                  )}
                </td>
              </tr>
            ) : (
              visibleAccounts.map((a) => (
                <tr key={a._id} className={a.is_active ? undefined : 'text-muted'}>
                  <td data-label="Name" className={a.is_active ? 'fw-semibold' : ''}>{a.name}</td>
                  <td data-label="Kind">
                    <Badge bg={KIND_VARIANTS[a.kind] || 'secondary'}>
                      {KIND_LABELS[a.kind] || a.kind || '—'}
                    </Badge>
                  </td>
                  <td data-label="Provider">{a.provider || '—'}</td>
                  <td data-label="Currency">{a.currency || '—'}</td>
                  <td data-label="Identifier">
                    {a.identifier ? (
                      <code className="small">{a.identifier}</code>
                    ) : '—'}
                  </td>
                  <td data-label="Active">
                    <Badge bg={a.is_active ? 'success' : 'secondary'} pill>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td data-label="Notes">
                    <small className="text-muted" title={a.notes || ''}>
                      {(() => {
                        if (!a.notes) return '—';
                        return a.notes.length > 40 ? `${a.notes.slice(0, 40)}…` : a.notes;
                      })()}
                    </small>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleOpenEdit(a)}
                    >
                      <i className="fa fa-pen me-1" />
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {modalOpen && (
        <AccountInputModal
          show={modalOpen}
          onHide={handleClose}
          account={modalAccount}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

export default Accounts;
